import { FC, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSnapshot } from "valtio";
import { appStore } from "@store/appStore.ts";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { EngagementService } from "@services/engagements/engagementService";
import { REUPLOAD_AUDIENCE_MEMBERS, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";

interface Props {
  audience: { id: number; name: string; launch_id: number; total_targetable?: number };
}

/*
    "Re-upload everyone" (MPN-1970). A republish of this audience's campaign that ignores
    which members are already on each network and sends the whole targetable membership
    with today's contacts, then rewrites the member links from what was sent.

    It exists for the repair path: a member linked under a broken field map, or one who has
    gained a second contact since, is otherwise never sent again. The holdout is untouched,
    unlike Danger Zone "Remove everything", which re-draws it. Staff only (Platform Admin,
    the same check the server makes) and behind its release toggle.
*/
export const ReuploadEveryoneButton: FC<Props> = ({ audience }) => {
  const enabled = useFeatureFlag(REUPLOAD_AUDIENCE_MEMBERS);
  const engSnap = useSnapshot(engagementStore);
  const isStaff = appStore.loggedInUser?.permissions?.hasSomeAccess?.("Platform Admin") ?? false;

  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<{ kind: "info" | "danger"; text: string } | null>(null);

  if (!enabled || !isStaff) return null;

  const count = (audience.total_targetable ?? 0).toLocaleString();

  async function reupload() {
    setRunning(true);
    try {
      const job = await new EngagementService().publishEngagement(engSnap.selectedEngagement.id, {
        launchIds: [audience.launch_id],
        reuploadAudienceIds: [audience.id],
      });
      setMessage({ kind: "info", text: `Re-upload started (job #${job.id}).` });
      setConfirming(false);
    } catch (e) {
      setMessage({ kind: "danger", text: e instanceof Error ? e.message : "Re-upload failed" });
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline-warning"
        style={{ fontSize: "0.72rem" }}
        onClick={() => {
          setMessage(null);
          setConfirming(true);
        }}
      >
        Re-upload everyone…
      </button>
      {message && (
        <span className={`small text-${message.kind}`} role="status">
          {message.text}
        </span>
      )}

      <Modal show={confirming} onHide={() => !running && setConfirming(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Re-upload everyone in {audience.name}?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          <p>
            This republishes the campaign and sends all <strong>{count}</strong> targetable members of{" "}
            <strong>{audience.name}</strong> to every network it publishes to, with their current
            contacts, whether or not they were uploaded before.
          </p>
          <ul className="mb-0">
            <li>Holdout members stay excluded. The holdout is not re-drawn.</li>
            <li>Networks dedupe contacts, so re-sending people already there is safe.</li>
            <li>The campaign must be marked ready to publish.</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setConfirming(false)}
            disabled={running}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-sm btn-warning" onClick={reupload} disabled={running}>
            {running ? "Starting…" : `Re-upload ${count} members`}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
